import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Chip, Select, SelectItem, Textarea } from "@nextui-org/react";
import { LuCheck, LuCircle, LuHistory, LuLoader2, LuSparkles } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import ProposalPreview from "./ProposalPreview";
import myAxios from "../../../utils/axiosWrapper";

const DIRECTIONS = [
  { key: "all", label: "Tous les appels" },
  { key: "incoming", label: "Appels entrants" },
  { key: "outgoing", label: "Appels sortants" },
];

const LOOKBACKS = [
  { key: "30", label: "30 derniers jours" },
  { key: "60", label: "60 derniers jours" },
  { key: "90", label: "90 derniers jours" },
  { key: "180", label: "180 derniers jours" },
];

const STATUS_CHIPS = {
  queued: { label: "En file d'attente", color: "primary" },
  running: { label: "En cours", color: "primary" },
  pending: { label: "À valider", color: "warning" },
  applied: { label: "Appliquée", color: "success" },
  rejected: { label: "Rejetée", color: "default" },
  cancelled: { label: "Annulée", color: "default" },
  failed: { label: "Échec", color: "danger" },
};

const ACTIVE_STATUSES = ["queued", "running"];
const REVIEWABLE_STATUSES = ["pending", "applied", "rejected"];
const POLL_INTERVAL_MS = 2500;

const STEP_ORDER = ["sampling", "mapping", "proposing", "validating", "dry_run"];

const stepLabel = (step, progress, t) => {
  switch (step) {
    case "sampling": {
      const stats = progress.sample_stats;
      return stats
        ? `${t("Échantillon")} : ${stats.size} ${t("appels")} · ${stats.operators_count} ${t(
            "opérateurs"
          )} · ${Object.keys(stats.by_purpose || {}).length} ${t("motifs")}`
        : t("Sélection d'un échantillon d'appels diversifié");
    }
    case "mapping":
      return progress.mapping
        ? `${t("Lecture des résumés d'appels")} (${progress.mapping.done}/${
            progress.mapping.total
          } ${t("lots")})`
        : t("Lecture des résumés d'appels");
    case "proposing":
      return t("Conception des champs et de la règle de notation");
    case "validating":
      return progress.repair_errors?.length
        ? `${t("Vérification")} · ${t("correction de")} ${progress.repair_errors.length} ${t(
            "point(s)"
          )}`
        : t("Vérification des contraintes du pipeline");
    case "dry_run":
      return progress.dry_run_total
        ? `${t("Test sur")} ${progress.dry_run_total} ${t("appels réels")}`
        : t("Test sur des appels réels");
    default:
      return step;
  }
};

// Progress of a queued/running/failed job, read from the job document.
const JobProgress = ({ job, onCancel, isCancelling, t }) => {
  const progress = job.progress || {};
  const step = progress.step === "repairing" ? "validating" : progress.step;
  const currentIndex = STEP_ORDER.indexOf(step);
  const isActive = ACTIVE_STATUSES.includes(job.status);
  return (
    <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-medium text-[#171717]">
          {t("Génération pour")} {job.department_name}
          <span className="ml-2 text-xs font-normal text-[#737373]">
            {isActive
              ? t("Vous pouvez quitter la page : la génération continue sur le serveur.")
              : ""}
          </span>
        </p>
        {isActive && (
          <Button
            size="sm"
            variant="flat"
            onClick={onCancel}
            isLoading={isCancelling}
          >
            {t("Annuler")}
          </Button>
        )}
      </div>
      <div className="space-y-2.5">
        {job.status === "queued" && (
          <div className="flex items-center gap-3 text-sm text-[#171717]">
            <LuLoader2
              size={16}
              className="text-primary animate-spin"
            />
            {t("En attente de démarrage")}
          </div>
        )}
        {STEP_ORDER.map((stepKey, index) => {
          const isDone = currentIndex >= 0 && index < currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div
              key={stepKey}
              className={`flex items-center gap-3 text-sm ${
                isDone || isCurrent ? "text-[#171717]" : "text-[#A3A3A3]"
              }`}
            >
              {isDone && (
                <LuCheck
                  size={16}
                  className="text-emerald-600"
                />
              )}
              {isCurrent && isActive && (
                <LuLoader2
                  size={16}
                  className="text-primary animate-spin"
                />
              )}
              {!isDone && !(isCurrent && isActive) && <LuCircle size={16} />}
              {stepLabel(stepKey, progress, t)}
            </div>
          );
        })}
      </div>
      {job.status === "failed" && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {job.error || t("La génération a échoué.")}
        </p>
      )}
      {job.status === "cancelled" && (
        <p className="mt-3 text-xs text-[#737373]">{t("Génération annulée.")}</p>
      )}
    </div>
  );
};

const AiGenerationPanel = ({ apiUrl, organisations, blockingReason, onConfigApplied }) => {
  const { t } = useTranslation();
  const departments = organisations?.departments || [];
  const [searchParams, setSearchParams] = useSearchParams();
  // The open job lives in the URL so a refresh reattaches to it.
  const jobId = searchParams.get("proposal");

  const [departmentId, setDepartmentId] = useState("");
  const [callDirection, setCallDirection] = useState("all");
  const [lookbackDays, setLookbackDays] = useState("60");
  const [description, setDescription] = useState("");
  const [history, setHistory] = useState([]);
  const [job, setJob] = useState(null);
  const [existingFields, setExistingFields] = useState([]);
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const pollTimerRef = useRef(null);

  const setJobId = (id) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id) next.set("proposal", id);
        else next.delete("proposal");
        return next;
      },
      { replace: true }
    );

  const fetchHistory = async (deptId = departmentId) => {
    if (!deptId) {
      setHistory([]);
      return;
    }
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals`,
        { params: { department_id: deptId } }
      );
      setHistory(response.data.proposals || []);
    } catch (e) {
      setHistory([]);
    }
  };

  const fetchExistingFields = async () => {
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/admin/get_company_custom_analysis_fields`
      );
      setExistingFields(
        (response?.data?.[0]?.custom_fields || []).filter(
          (field) => !field.deleted && field.enabled !== false
        )
      );
    } catch (e) {
      setExistingFields([]);
    }
  };

  const fetchJob = async (id) => {
    const response = await myAxios.get(
      `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${id}`
    );
    return response.data.proposal;
  };

  useEffect(() => {
    fetchExistingFields();
  }, []);

  useEffect(() => {
    fetchHistory(departmentId);
  }, [departmentId]);

  // Load the job named in the URL, and poll it while it runs.
  useEffect(() => {
    let isCancelled = false;
    clearTimeout(pollTimerRef.current);
    if (!jobId) {
      setJob(null);
      return undefined;
    }

    const load = async () => {
      try {
        const loaded = await fetchJob(jobId);
        if (isCancelled) return;
        setJob(loaded);
        setError(null);
        setDepartmentId((current) => current || loaded.department_id);
        if (ACTIVE_STATUSES.includes(loaded.status)) {
          pollTimerRef.current = setTimeout(load, POLL_INTERVAL_MS);
        } else {
          fetchHistory(loaded.department_id);
        }
      } catch (e) {
        if (isCancelled) return;
        if (e?.response?.status === 404) {
          setError(t("Impossible de charger la proposition."));
          setJobId(null);
        } else {
          // Possibly transient (network, deploy): say so, and keep retrying.
          setError(t("Impossible de charger la proposition, nouvel essai en cours…"));
          pollTimerRef.current = setTimeout(load, POLL_INTERVAL_MS * 2);
        }
      }
    };
    load();
    return () => {
      isCancelled = true;
      clearTimeout(pollTimerRef.current);
    };
  }, [jobId]);

  const handleGenerate = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const response = await myAxios.post(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/generate`,
        {
          department_id: departmentId,
          call_direction: callDirection,
          description,
          lookback_days: parseInt(lookbackDays, 10),
        }
      );
      setJobId(response.data.proposal_id);
      fetchHistory(departmentId);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.proposal_id) {
        // One already running for this department: follow it.
        setJobId(detail.proposal_id);
      } else {
        setError(
          (typeof detail === "string" ? detail : detail?.message) ||
            t("Impossible de lancer la génération.")
        );
      }
    } finally {
      setIsStarting(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await myAxios.post(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${jobId}/cancel`
      );
      setJob(await fetchJob(jobId));
      fetchHistory(departmentId);
    } catch (e) {
      setError(t("Impossible d'annuler la génération."));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleApplied = () => {
    fetchExistingFields();
    fetchHistory(departmentId);
    setJob((previous) => ({ ...previous, status: "applied" }));
    onConfigApplied();
  };

  const handleRejected = () => {
    fetchHistory(departmentId);
    setJobId(null);
  };

  const isReviewable = job && REVIEWABLE_STATUSES.includes(job.status);

  return (
    <div>
      <div className="flex items-center gap-2">
        <LuSparkles
          className="text-violet-600"
          size={20}
        />
        <h2 className="text-lg font-semibold text-[#171717]">
          {t("Générer une configuration avec l'IA")}
        </h2>
      </div>
      <p className="text-xs text-[#737373] mt-1 max-w-3xl">
        {t(
          "L'IA lit les résumés d'un échantillon diversifié d'appels récents du département, propose des champs d'analyse et une règle de notation adaptés à son métier, puis les teste sur des appels réels. Rien n'est modifié avant votre validation."
        )}
      </p>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select
              size="sm"
              label={t("Département")}
              selectedKeys={departmentId ? new Set([departmentId]) : new Set()}
              onSelectionChange={(keys) => setDepartmentId(Array.from(keys)[0] || "")}
            >
              {departments.map((department) => (
                <SelectItem
                  key={department.id}
                  value={department.id}
                >
                  {department.name}
                </SelectItem>
              ))}
            </Select>
            <Select
              size="sm"
              label={t("Sens d'appel")}
              selectedKeys={new Set([callDirection])}
              onSelectionChange={(keys) => setCallDirection(Array.from(keys)[0] || "all")}
            >
              {DIRECTIONS.map((direction) => (
                <SelectItem
                  key={direction.key}
                  value={direction.key}
                >
                  {t(direction.label)}
                </SelectItem>
              ))}
            </Select>
            <Select
              size="sm"
              label={t("Période analysée")}
              selectedKeys={new Set([lookbackDays])}
              onSelectionChange={(keys) => setLookbackDays(Array.from(keys)[0] || "60")}
            >
              {LOOKBACKS.map((lookback) => (
                <SelectItem
                  key={lookback.key}
                  value={lookback.key}
                >
                  {t(lookback.label)}
                </SelectItem>
              ))}
            </Select>
          </div>
          <Textarea
            label={t("Description du département (optionnel)")}
            placeholder={t(
              "Ex. : Service après-vente. Gère les retards de livraison, retours et pannes matériel des cabinets. Objectif : résoudre au premier appel et donner un délai précis au client."
            )}
            value={description}
            onValueChange={setDescription}
            minRows={3}
            maxLength={4000}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              color="primary"
              startContent={!isStarting && <LuSparkles size={16} />}
              onClick={handleGenerate}
              isLoading={isStarting}
              isDisabled={!departmentId || isStarting}
            >
              {t("Générer")}
            </Button>
          </div>
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </p>
          )}
          {job && !isReviewable && (
            <JobProgress
              job={job}
              onCancel={handleCancel}
              isCancelling={isCancelling}
              t={t}
            />
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-[#171717]">
            <LuHistory size={16} />
            {t("Propositions précédentes")}
          </div>
          {!departmentId && (
            <p className="mt-3 text-xs text-[#A3A3A3]">{t("Choisissez un département.")}</p>
          )}
          {departmentId && history.length === 0 && (
            <p className="mt-3 text-xs text-[#A3A3A3]">{t("Aucune proposition pour l'instant.")}</p>
          )}
          <div className="mt-2 divide-y divide-gray-100">
            {history.map((item) => {
              const status = STATUS_CHIPS[item.status] || {
                label: item.status,
                color: "default",
              };
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setJobId(item.id)}
                  className={`w-full text-left py-2.5 flex items-center justify-between gap-2 hover:bg-gray-50 ${
                    jobId === item.id ? "bg-violet-50" : ""
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-xs text-[#171717]">
                      {new Date(item.created_at).toLocaleString()}
                    </p>
                    <p className="text-[11px] text-[#A3A3A3] truncate">
                      {t(DIRECTIONS.find((d) => d.key === item.call_direction)?.label || "")}
                      {item.sample_stats?.size ? ` · ${item.sample_stats.size} ${t("appels")}` : ""}
                      {item.created_by ? ` · ${item.created_by}` : ""}
                    </p>
                  </div>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={status.color}
                  >
                    {t(status.label)}
                  </Chip>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {isReviewable && (
        <ProposalPreview
          apiUrl={apiUrl}
          proposal={{ ...job, id: jobId }}
          existingFields={existingFields}
          blockingReason={blockingReason}
          onApplied={handleApplied}
          onRejected={handleRejected}
          onClose={() => setJobId(null)}
        />
      )}
    </div>
  );
};

export default AiGenerationPanel;
